import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowEvent,
  DataTableStateEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyScreen from '../../../../../../../../components/empty-screen/EmptyScreen';
import { useGetPaginatedProductTasks } from '../../../../../../../../hooks/api/request-pcf';
import { useProductTasksTableQueryParams } from '../../../../../../../../hooks/useProductTasksTableQueryParams';
import { Locale } from '../../../../../../../../i18n';
import { COMPONENT_TASKS_COMPLETE_STATUS_MAPPING } from '../../../../../../../../shared/constants/tasks.constant';
import { PCFRequestStatus } from '../../../../../../../../shared/enums/pcf-request';
import { getPaginatorTemplate } from '../../../../../../../../shared/helpers/get-paginator-template';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import { IRequest } from '../../../../../../../../shared/interfaces/IRequest';
import TaskStatus from '../../../../../task-status/TaskStatus';
import ProductComponentId from '../../../components/product-component-id/ProductComponentId';
import ProductComponentName from '../../../components/product-component-name/ProductComponentName';
import AnswerButton from '../answer-button/AnswerButton';
import ProductTasksTableHeader from '../product-tasks-table-header/ProductTasksTableHeader';
import styles from './ProductTasksTable.module.css';

const ProductTasksTable = (): JSX.Element => {
  const { setParams, skip, size, sort, filters } = useProductTasksTableQueryParams();
  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const {
    i18n: { language },
    t,
  } = useTranslation();

  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const { data, isLoading } = useGetPaginatedProductTasks({
    skip,
    size,
    sort,
    ...filters,
  });

  useEffect(() => {
    if (data) {
      setTotalRecords(data?.pagination?.total || 0);
    }
  }, [data]);

  const productIdHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{t('tasksPage.tabs.productTasksTab.productTasksTableColumns.productId')}</span>
        <span className='text-sm font-light font-italic text-gray-700'>
          {t('tasksPage.tabs.productTasksTab.productTasksTableColumns.requestedId')}
        </span>
      </div>
    );
  };

  const productNameHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{t('tasksPage.tabs.productTasksTab.productTasksTableColumns.productName')}</span>
        <span className='text-sm font-light font-italic text-gray-700'>
          {t('tasksPage.tabs.productTasksTab.productTasksTableColumns.requestedName')}
        </span>
      </div>
    );
  };

  const productNameBodyTemplate = (rowData: IRequest): JSX.Element => {
    let productName;
    let requestedProductName;

    // Case 1: If it is additional component request and has been answered, stack both names
    if (rowData.product && rowData.otherRequestData) {
      productName = rowData.product.productName;
      requestedProductName = rowData.otherRequestData.component_name;
    }
    // Case 2: If it is additional component request and hasn't been answered, show the requested name as main name
    else if (rowData.otherRequestData) {
      productName = rowData.otherRequestData.component_name;
    }
    // Case 3: If it is not additional component request, show the product name as main name
    else {
      productName = rowData?.product?.productName;
    }

    return <ProductComponentName name={productName} requestedName={requestedProductName} />;
  };

  const productIdBodyTemplate = (rowData: IRequest): JSX.Element => {
    let productId;
    let requestedProductId;

    // Case 1: If it is additional component request and has been answered, stack both names
    if (rowData.product && rowData.otherRequestData) {
      productId = rowData.product.productId;
      requestedProductId = rowData.otherRequestData.component_id;
    }
    // Case 2: If it is additional component request and hasn't been answered, show the requested name as main name
    else if (rowData.otherRequestData) {
      productId = rowData.otherRequestData.component_id;
    }
    // Case 3: If it is not additional component request, show the product name as main name
    else {
      productId = rowData?.product?.productId;
    }

    return <ProductComponentId id={productId} requestedId={requestedProductId} />;
  };

  const companyNameTemplate = (rowData: IRequest): JSX.Element => {
    return <span>{rowData.company?.companyName ?? rowData.company?.companyBpn}</span>;
  };

  const lastUpdatedTemplate = (rowData: IRequest): React.ReactNode => {
    // Determine lastUpdated date based on several conditions
    let lastUpdated;

    // Determine lastUpdated based on status and isShared
    if (COMPONENT_TASKS_COMPLETE_STATUS_MAPPING.includes(rowData.status)) {
      if (rowData.isShared) {
        // If the request is shared, use dateCreated
        lastUpdated = rowData.dateCreated;
      } else {
        // If the request is not shared, use dateAnswered
        lastUpdated = rowData.dateAnswered;
      }
    } else {
      // For non-complete statuses, use dateStatusChanged
      lastUpdated = rowData.dateStatusChanged;
    }

    // Fallback to dateCreated if lastUpdated is undefined
    return parseDateWithoutTime(lastUpdated || rowData.dateCreated, language as Locale);
  };

  const statusTemplate = (rowData: IRequest): JSX.Element => {
    return <TaskStatus status={rowData.status} taskType='product' />;
  };

  const answerTemplate = (rowData: IRequest): JSX.Element | null => {
    if (rowData.status === PCFRequestStatus.Pending) {
      return <AnswerButton productTask={rowData} />;
    }

    return null;
  };

  const rowExpansionTemplate = (data: IRequest): JSX.Element => {
    // Check if it is a manually entered company when sharing a PCF, then show the email
    const isManualCompany = data.isShared && data.otherRequestData?.email;

    if (isManualCompany) {
      return (
        <div
          className={classNames(
            styles['comment-expansion-container'],
            'flex flex-column w-full gap-1',
          )}
        >
          <span>{t('global.sentTo')}</span>
          <span>{data?.otherRequestData?.email}</span>
        </div>
      );
    }

    // Otherwise, show the comment
    return (
      <div
        className={classNames(
          styles['comment-expansion-container'],
          'flex flex-column w-full gap-1',
        )}
      >
        <span>{t('global.comment')}</span>
        <span>{data.message}</span>
      </div>
    );
  };

  const onRowToggle = (event: DataTableRowEvent): void => {
    setExpandedRows(event.data);
  };

  const allowExpansion = (rowData: IRequest) => {
    // Show expansion button only if there is a message (comment) or the target company is manually entered
    const isManualCompany = rowData.isShared && rowData.otherRequestData?.email;

    return Boolean(!!rowData.message || isManualCompany);
  };

  const isTableEmpty =
    !isLoading &&
    data?.data.length === 0 &&
    Object.values(filters).filter((value) => {
      return Boolean(value);
    }).length === 0;

  if (isTableEmpty) {
    return (
      <EmptyScreen>
        <p className='flex text-center font-light'>
          {t('tasksPage.tabs.productTasksTab.emptyScreenText')}
        </p>
      </EmptyScreen>
    );
  }

  return (
    <div className={styles['product-tasks-table']}>
      <DataTable
        value={data?.data}
        scrollable
        rowExpansionTemplate={rowExpansionTemplate}
        expandedRows={expandedRows}
        onRowToggle={onRowToggle}
        loading={isLoading}
        header={<ProductTasksTableHeader />}
        lazy
        rows={size}
        paginatorPosition='top'
        paginator
        sortField={sortField}
        sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
        onSort={(event: DataTableStateEvent) => {
          const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
          setParams({
            sort: `${event.sortField}-${newSortOrder}`,
            page: 1,
          });
        }}
        paginatorTemplate={getPaginatorTemplate({
          totalRecords,
          rowsPerPage: size,
          skip,
          onNext: () => {
            setParams({ page: (skip + size) / size + 1 });
          },
          onPrev: () => {
            setParams({ page: (skip + size) / size - 1 });
          },
        })}
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '1200px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          expander={allowExpansion}
          headerStyle={{ width: '60px', maxWidth: '60px' }}
          style={{ width: '60px', maxWidth: '60px' }}
        />

        <Column
          header={productNameHeaderTemplate}
          body={productNameBodyTemplate}
          sortable
          sortField='productName'
          style={{ width: '20%', wordBreak: 'break-word' }}
        />

        <Column
          header={productIdHeaderTemplate}
          body={productIdBodyTemplate}
          sortable
          sortField='productId'
          style={{ width: '20%', wordBreak: 'break-word' }}
        />

        <Column
          header={t('tasksPage.tabs.productTasksTab.productTasksTableColumns.companyName')}
          body={companyNameTemplate}
          sortable
          sortField='companyName'
          style={{ width: '20%', wordBreak: 'break-word' }}
        />

        <Column
          header={t('tasksPage.tabs.productTasksTab.productTasksTableColumns.lastUpdated')}
          body={lastUpdatedTemplate}
          sortable
          sortField='lastUpdated'
          style={{ width: '12%' }}
        />

        <Column
          header={t('tasksPage.tabs.productTasksTab.productTasksTableColumns.status')}
          body={statusTemplate}
          sortable
          sortField='status'
          style={{ width: '12%' }}
        />

        <Column body={answerTemplate} />
      </DataTable>
    </div>
  );
};

export default ProductTasksTable;
